/**
 * Parent cookie domain for cross-subdomain auth handoff (vd_ctx_tmp, userContext, etc.).
 * Prefer hostname-derived domain so a wrong VITE_BASE_DOMAIN build arg cannot break login redirects.
 */

import { isStagingDeployHostname } from '@/utils/staging-domain.util'
import { resolveDeployBaseDomainFromHostname } from '@/utils/deploy-domain.util'

/** Correct the common Vidhyantra domain typo (vidyantra → vidhyantra). */
export function normalizeVidhyantraDomain(value: string | undefined | null): string {
  if (!value?.trim()) return value?.trim() ?? ''
  return value.replace(/vidyantra(?=\.|$|:|\/)/gi, 'vidhyantra')
}

/** True for localhost, 127.0.0.1, *.localhost, or *.vidhyantra.local (incl. vidyantra typo). */
export function isLocalDevHostname(hostname?: string): boolean {
  const h = normalizeVidhyantraDomain(
    hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '')
  )
  if (!h) return false
  return (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h.endsWith('.localhost') ||
    h === 'vidhyantra.local' ||
    h.endsWith('.vidhyantra.local')
  )
}

/**
 * Cookie Domain attribute for sharing across subdomains of the current site.
 * e.g. platform.staging.vidhyantra.com + api.staging.vidhyantra.com → `.staging.vidhyantra.com`
 */
export function getParentCookieDomain(): string | undefined {
  if (typeof window === 'undefined') return undefined

  const hostname = normalizeVidhyantraDomain(window.location.hostname)

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return undefined
  }
  if (hostname.endsWith('.localhost') || hostname.endsWith('.127.0.0.1')) {
    return '.localhost'
  }

  const deployBase = resolveDeployBaseDomainFromHostname(hostname)
  if (deployBase) {
    return `.${deployBase}`
  }

  const envDomain = normalizeVidhyantraDomain(import.meta.env.VITE_COOKIE_DOMAIN)
  if (envDomain) {
    return envDomain.startsWith('.') ? envDomain : `.${envDomain}`
  }

  if (isStagingDeployHostname(hostname)) {
    return '.staging.vidhyantra.com'
  }

  const parts = hostname.split('.')
  if (parts.length >= 2) {
    return `.${parts.slice(-2).join('.')}`
  }

  const envBase = normalizeVidhyantraDomain(import.meta.env.VITE_BASE_DOMAIN)
  if (envBase && envBase !== 'localhost') {
    return `.${envBase}`
  }

  return undefined
}
